/* eslint-disable complexity */
import { BenefitType } from '@strata/constants';
import { useDebouncedFunction } from '@strata/helper-functions';
import { ChatWithConversationPane } from '@strata/react-chat';
import { Spinner } from '@strata/react-loading-animation';
import { SuccessToast } from '@strata/react-toast-message';
import { Dispatch, SetStateAction, useState } from 'react';

import { PerformanceRequirement } from 'api/performanceAPI';
import { Conversation, createConversation, findConversations } from 'api/userSupportAPI';
import { NewConversationDialog } from 'components/wonReferrals/dialogs/NewConversationDialog';
import { useAuthProfile, useUserProfile, useUserSupportMessages } from 'services/APIHooks';
import { handleError } from 'services/Error';
import { AppRoutes } from 'services/routes';
import {
    ConversationContext,
    detailFactsSort,
    isFactValid,
    mapConversation,
    mapDatumValue,
    MappedConversation,
} from 'services/userSupportHelpers';
import { store, useAppDispatch, useAppSelector } from 'store';
import { useRedirectedConversation } from 'store/userSupport/userSupportHooks';
import { updateCurrentConversation } from 'store/userSupport/userSupportSlice';

interface MessagesProps {
    referralId: string;
    referralFriendlyId: string;
    memberFirstName?: string;
    memberLastName?: string;
    benefit: BenefitType;
    segmentId: string;
    conversations?: MappedConversation[];
    performanceEntries?: PerformanceRequirement[];
    showNewConversationDialog?: boolean | ConversationContext;
    setShowNewConversationDialog: Dispatch<SetStateAction<boolean | ConversationContext>>;
}

export const Messages = ({
    referralId,
    referralFriendlyId,
    memberFirstName = undefined,
    memberLastName = undefined,
    benefit,
    segmentId,
    conversations = undefined,
    performanceEntries = undefined,
    showNewConversationDialog = undefined,
    setShowNewConversationDialog,
}: MessagesProps) => {
    const dispatch = useAppDispatch();
    const selectedConversationId = useAppSelector((state) => state.userSupport.currentConversation);
    const [searchResults, setSearchResults] = useState<MappedConversation[]>();
    const { messages, sendMessage, facts } = useUserSupportMessages();
    const [chatLoading, setChatLoading] = useState(false);
    const authProfile = useAuthProfile(true);
    const supplierNpi = authProfile?.organizations?.[0]?.supplierNpi;
    const userProfile = useUserProfile();
    const [searchTerm, setSearchTerm] = useState<string>();
    const debouncedSearch = useDebouncedFunction();

    const selectedConversation = conversations?.find(
        (conv) => conv.conversationId === selectedConversationId,
    );

    const setMappedSearchResults = (conversationsList: Conversation[]) => {
        const mappedConversations = conversationsList.map(mapConversation);
        setSearchResults(mappedConversations);
    };

    useRedirectedConversation(conversations);

    const canCallSocket = (socketCall: () => void) => {
        const { userSupport } = store.getState();
        const { socketConnected } = userSupport;
        if (socketConnected) {
            socketCall();
            return true;
        }
        return false;
    };

    const makeSocketCall = async (socketCall: () => void) => {
        if (canCallSocket(socketCall)) {
            return Promise.resolve(true);
        }
        setChatLoading(true);
        return new Promise((resolve) => {
            const timer = setInterval(() => {
                if (canCallSocket(socketCall)) {
                    clearInterval(timer);
                    setChatLoading(false);
                    resolve(true);
                }
            }, 5000);
        });
    };

    if (!referralId || !conversations || !userProfile) {
        return (
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    height: '100%',
                }}
            >
                <Spinner size={Spinner.Size.LARGE} intent={Spinner.Intent.NONE} />
            </div>
        );
    }

    const conversationsList = searchResults ?? conversations ?? [];

    return (
        <>
            <NewConversationDialog
                openWithContext={showNewConversationDialog}
                performanceEntries={performanceEntries}
                onCancel={() => setShowNewConversationDialog(false)}
                onCreate={async ({ topic, facts: newFacts, details }) => {
                    if (!selectedConversation) {
                        setChatLoading(true);
                    }
                    try {
                        const newConversation = await createConversation({
                            referralId,
                            body: {
                                details: {
                                    created_by_upn: userProfile.upn,
                                    details,
                                    referral_friendly_id: referralFriendlyId,
                                    member_first_name: memberFirstName,
                                    member_last_name: memberLastName,
                                    supplier_npi: supplierNpi!,
                                    topic_id: topic.value,
                                    benefit,
                                    segment_id: segmentId,
                                },
                                data: Object.values(newFacts).reduce(
                                    (prev, curr) => {
                                        if (!isFactValid(curr, newFacts, details)) {
                                            return prev;
                                        }
                                        const commonValues = {
                                            topic_id: curr.topic_id,
                                            schema_id: curr.schema_id,
                                            fact_id: curr.fact_id,
                                        };
                                        if (Array.isArray(curr.datum_value)) {
                                            return [
                                                ...prev,
                                                ...curr.datum_value
                                                    .filter((datum) => datum !== null)
                                                    .map((value, index) => ({
                                                        ...commonValues,
                                                        datum_value: value,
                                                        datum_index: index,
                                                    })),
                                            ];
                                        }
                                        return [
                                            ...prev,
                                            {
                                                ...commonValues,
                                                datum_value: curr?.datum_value ?? null,
                                            },
                                        ];
                                    },
                                    [] as {
                                        datum_id?: string;
                                        topic_id: string;
                                        schema_id: string;
                                        fact_id: string;
                                        datum_value?: string | null;
                                        datum_index?: number;
                                    }[],
                                ),
                            },
                        });
                        const newMappedConversation = mapConversation(newConversation);
                        await makeSocketCall(() =>
                            dispatch(
                                updateCurrentConversation({
                                    conversationId: newMappedConversation.conversationId,
                                }),
                            ),
                        );
                        SuccessToast({ message: `New conversation created: ${topic.name}.` });
                        setShowNewConversationDialog(false);
                    } catch (err) {
                        handleError('Unable to create a new conversation.', err as Error);
                        throw err;
                    }
                    setChatLoading(false);
                }}
            />
            <ChatWithConversationPane
                conversations={conversationsList}
                showViewDetailsButton
                userUPN={userProfile.upn}
                chatLinkGenerator={(conversation) =>
                    conversation?.displayId
                        ? `${window.location.origin}${AppRoutes.conversationDetails(
                              conversation?.displayId,
                          )}`
                        : undefined
                }
                conversationType={ChatWithConversationPane.ConversationType.CHAT}
                onSearch={(value) => {
                    if (value?.length > 0) {
                        debouncedSearch(() => {
                            (async () => {
                                try {
                                    const foundMessages = await findConversations({
                                        searchTerm: value,
                                        referralId,
                                        page: undefined,
                                    });
                                    setMappedSearchResults(foundMessages);
                                    setSearchTerm(value);
                                } catch (err) {
                                    handleError('Unable to search conversations.', err as Error);
                                }
                            })();
                        });
                    } else {
                        setSearchTerm(undefined);
                        setSearchResults(undefined);
                    }
                }}
                onConversationSelect={(conversationId) => {
                    (() =>
                        makeSocketCall(() =>
                            dispatch(updateCurrentConversation({ conversationId })),
                        ))();
                }}
                selectedConversation={
                    selectedConversation
                        ? {
                              ...selectedConversation,
                              messages: Object.values(messages ?? {}),
                              details: {
                                  ...selectedConversation.details,
                                  data: [...(facts ?? [])].sort(detailFactsSort).reduce(
                                      (prev, curr) => {
                                          const label = prev[curr.label]
                                              ? `${curr.label} ${(curr.datum_index ?? 0) + 1}`
                                              : curr.label;
                                          return {
                                              ...prev,
                                              [label]: mapDatumValue(selectedConversation, curr),
                                          };
                                      },
                                      {} as Record<string, string | undefined>,
                                  ),
                              },
                          }
                        : undefined
                }
                onNewConversationClick={() => setShowNewConversationDialog(true)}
                onMessageSend={async ({ conversationId, details }) => {
                    try {
                        await makeSocketCall(() =>
                            sendMessage({
                                conversation_id: conversationId,
                                details: {
                                    content: {
                                        message: details.message,
                                    },
                                },
                            }),
                        );
                        SuccessToast({ message: 'Message sent successfully.' });
                    } catch (err) {
                        handleError('Unable to save message.', err as Error);
                    }
                }}
                highlightText={searchTerm}
                chatLoading={chatLoading || !messages || !facts}
            />
        </>
    );
};
